"use client";

import { api } from "@v1/backend/convex/_generated/api";
import { AgentMarkLucideIcon } from "@v1/ui/agent-mark-icon";
import { cn } from "@v1/ui/utils";
import { useQuery } from "convex/react";
import { ChevronRight, Search, Send, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { type ReactElement, type ReactNode, useState } from "react";
import { type MessengerVisitorChannel, useMessenger } from "./messenger-context";
import { MessengerConversationRow } from "./messenger-lists";
import { useMessengerConversations } from "./use-messenger-conversations";
import { useMessengerVisitorChats } from "./use-messenger-visitor-chats";

const HOME_ARTICLES = 4;
const HOME_CHATS = 3;

function Card({ children, className }: { readonly children: ReactNode; readonly className?: string }): ReactElement {
  return (
    <section className={cn("overflow-hidden rounded-xl bg-card shadow-[0_2px_12px_rgba(0,0,0,0.06)] ring-1 ring-border/50", className)}>
      {children}
    </section>
  );
}

function RecentConversation(): ReactElement | null {
  const t = useTranslations("messenger.home");
  const { rows } = useMessengerConversations(3);
  const row = rows[0];
  if (row === undefined) return null;
  return (
    <Card>
      <p className="px-4 pt-3 text-[12px] font-medium text-primary/50">{t("letzteNachricht")}</p>
      <MessengerConversationRow row={row} compact />
    </Card>
  );
}

/** Visitor has no Nachrichten tab: their chats from this browser live here. */
function VisitorChats({ channel }: { readonly channel: MessengerVisitorChannel }): ReactElement | null {
  const t = useTranslations("messenger.home");
  const rows = useMessengerVisitorChats(channel);
  const [all, setAll] = useState<boolean>(false);
  if (rows === undefined || rows.length === 0) return null;
  const shown = all ? rows : rows.slice(0, HOME_CHATS);
  return (
    <Card>
      <p className="px-4 pt-3 text-[12px] font-medium text-primary/50">{t("deineChats")}</p>
      <ul className="divide-y divide-border/40">
        {shown.map((row) => (
          <li key={row.key}>
            <MessengerConversationRow row={row} />
          </li>
        ))}
      </ul>
      {rows.length > shown.length ? (
        <button
          type="button"
          onClick={() => setAll(true)}
          className="flex h-10 w-full items-center justify-center border-t border-border/40 text-[13px] font-medium text-action-700 transition-colors hover:bg-action-500/[0.04]"
        >
          {t("alleChats")}
        </button>
      ) : null}
    </Card>
  );
}

function HelpCard(): ReactElement | null {
  const t = useTranslations("messenger.home");
  const { config, nav } = useMessenger();
  const center = useQuery(api.pages.public_help.getPublicHelpCenter, { org_slug: config.help_org_slug });
  if (center === undefined || center.state !== "ok") return null;
  const pages = center.spaces.flatMap((space) => space.pages.filter((p) => p.depth === 0)).slice(0, HOME_ARTICLES);
  if (pages.length === 0) return null;
  return (
    <Card>
      <button
        type="button"
        onClick={() => nav.selectTab("help")}
        className="m-2 flex h-10 w-[calc(100%-1rem)] items-center gap-2 rounded-lg bg-action-500/[0.05] px-3 text-left text-[13.5px] text-primary/45 transition-colors hover:bg-action-500/[0.08]"
      >
        <span className="min-w-0 flex-1 truncate">{t("hilfeDurchsuchen")}</span>
        <Search className="h-4 w-4 shrink-0 text-action-600" strokeWidth={2} aria-hidden />
      </button>
      <ul className="pb-2">
        {pages.map((page) => (
          <li key={page.page_id}>
            <button
              type="button"
              onClick={() => nav.push({ kind: "article", pageId: page.page_id })}
              className="flex min-h-10 w-full items-center gap-2 px-4 py-2 text-left text-[13.5px] text-primary/80 transition-colors hover:bg-action-500/[0.04]"
            >
              <span className="min-w-0 flex-1 truncate">{page.title}</span>
              <ChevronRight className="h-4 w-4 shrink-0 text-action-600" strokeWidth={2} aria-hidden />
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Start: greeting and „Nachricht senden“ on the brand colour, then cards (chats, help). */
export function MessengerHomeView(): ReactElement {
  const t = useTranslations("messenger");
  const { audience, config, nav, close } = useMessenger();
  return (
    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
      <div className="bg-gradient-to-b from-action-600 to-action-500 px-6 pb-6 pt-5 text-white">
        <div className="flex h-9 items-center justify-between">
          <span className="flex size-9 items-center justify-center rounded-xl bg-white/15">
            <AgentMarkLucideIcon className="h-4 w-4 text-white" />
          </span>
          {close !== null ? (
            <button
              type="button"
              aria-label={t("header.schliessen")}
              onClick={close}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-white/80 transition-colors hover:bg-white/15 hover:text-white"
            >
              <X className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
            </button>
          ) : null}
        </div>
        <p className="mt-9 text-[24px] font-semibold leading-tight text-white/65">
          {config.first_name ? t("home.hallo", { name: config.first_name }) : t("home.halloOhneName")}
        </p>
        <p className="text-[24px] font-semibold leading-tight text-white">{t("home.frage")}</p>
        {/* Whole bar inside the colour: half over the edge looked broken. */}
        <button
          type="button"
          onClick={() => nav.push({ kind: "chat", sessionId: null })}
          className="mt-6 flex h-12 w-full items-center gap-3 rounded-xl bg-card px-4 text-left shadow-[0_2px_12px_rgba(0,0,0,0.08)] transition-colors hover:bg-card/95"
        >
          <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-primary/90">{t("home.nachrichtSenden")}</span>
          <Send className="h-4 w-4 shrink-0 text-action-600" strokeWidth={2} aria-hidden />
        </button>
      </div>
      <div className="flex flex-col gap-3 px-4 py-4">
        {audience.kind === "member" ? <RecentConversation /> : <VisitorChats channel={audience.channel} />}
        <HelpCard />
      </div>
    </div>
  );
}
